'use client'

import { CMS_INSTANCE } from '@/lib/api'
import { useRouter } from '@/navigation'
import useAuth from '@/store/auth'
import useFpd from '@/store/fpd'
import useMeta from '@/store/meta'
import { findParentWithDataAttribute } from '@/utils/dom-utils'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { sendGTMEvent } from '@next/third-parties/google'
import dayjs from 'dayjs'
import { useLocale, useTranslations } from 'next-intl'
import { useReCaptcha } from 'next-recaptcha-v3'
import { redirect, usePathname, useSearchParams } from 'next/navigation'
import { SnackbarProvider } from 'notistack'
import { useEffect } from 'react'
import { SWRConfig } from 'swr'
// import InjectGTM from '@/lib/gtm'
import getHospitalLink from '@/utils/hospital/getHospitalLink'
import isActivateUserAccount from '@/utils/isActivateUserAccount'
import isHospitalActivated from '@/utils/isHospitalActivated'
import { deleteCookie, getCookie } from 'cookies-next'
import 'dayjs/locale/th'
import buddhistEra from 'dayjs/plugin/buddhistEra'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import localeData from 'dayjs/plugin/localeData'

const ClientStoreInitializer = ({ meta, children }) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const locale = useLocale()
  const __t = useTranslations()
  const bypassHospital = searchParams.get('bypass')
  const { executeRecaptcha } = useReCaptcha()
  // const [isGoogle, setIsGoogle] = useState(false)
  dayjs.extend(buddhistEra)
  dayjs.extend(localeData)
  dayjs.extend(customParseFormat)
  dayjs.locale(locale)
  let dateFormats =
    locale === 'th'
      ? {
          year: 'YYYY', // Year when select day => month => this year (BBBB didnt work)
          monthAndYear: 'MMMM BBBB', // Year show in select modal as dropdown (BBBB work)
          keyboardDate: 'DD/MM/YYYY', // year show in field (BBBB didnt work)
        }
      : {
          year: 'YYYY',
          monthAndYear: 'MMMM YYYY',
          keyboardDate: 'DD/MM/YYYY',
        }

  useEffect(() => {
    let centerGroups = meta?.center_groups
      ?.filter((x) => x.title)
      ?.filter(
        (x) =>
          !x.only_show_in ||
          x.only_show_in?.length === 0 ||
          x.only_show_in.includes(locale)
      )
    // Map centers id in centerGroups to int
    centerGroups = centerGroups?.map((x) => ({
      ...x,
      centers: x?.centers?.map((y) => parseInt(y)),
    }))
    // Manage redirect
    const hospitalSlug = meta?.meta_info?._slug
    const redirectLink =
      meta?.meta_info?.website_language_redirect ||
      meta?.meta_info?.website_redirect
    const isActiveOneWebsite = isHospitalActivated(meta?.meta_info)

    if (hospitalSlug !== 'group' && !bypassHospital) {
      // If hospital is not activate one website, redirect to website_redirect
      if (!isActiveOneWebsite && redirectLink) {
        redirect(redirectLink)
      }
      // If  meta_info is not found, redirect to home page
      if (!meta || !meta?.meta_info || !meta?.meta_info?.supported_languages) {
        if (typeof window !== 'undefined') {
          window.location.href = '/'
        }
      }
    }
    // If current locale is not supported, redirect to hospital home page
    if (
      meta?.meta_info?.supported_languages?.length &&
      locale &&
      !meta?.meta_info?.supported_languages?.includes(locale)
    ) {
      if (typeof window !== 'undefined') {
        window.location.href = getHospitalLink(hospitalSlug, '/')
      }
    }

    useMeta.setState({
      metaSettings: meta.settings,
      bFanBadges: meta.bfan_badges,
      metaInfo: meta.meta_info,
      centerGroups,
      centers: meta.centers,
      taxonomies: meta.taxonomies,
      seoRedirect: meta.seo_redirect,
      campusHospitals: meta.campus_hospitals,
      cancerTypes: meta?.cancer_types,
      hospitalsByZone: meta.hospitals_by_zone,
      hospitalsActivatedOneWebsite:
        meta.hospitals_activated_one_website?.filter(
          (x) => x?._slug !== 'group'
        ),
      allLocales: meta?.all_locales || [],
      metaIsLoading: false,
      isGroupPage:
        meta.meta_info?._slug === 'group' || meta.meta_info?.code === 'bhg',
    })
  }, [meta])

  useEffect(() => {
    // Prevent running on server-side
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return
    }

    ;(async () => {
      if (!isActivateUserAccount(meta?.meta_info)) {
        return
      }

      const userMe = await useAuth.getState().getUserMe({ locale })
      if (!userMe?.success) {
        return
      }
      if (!userMe?.data?.is_basic_filled || !userMe?.data?.is_consent) {
        const destination = searchParams.get('destination')
        if (destination && !destination.includes('/consent-form')) {
          return router.push(`/consent-form?destination=${destination}`)
        }
        if (pathname && !pathname.includes('/consent-form')) {
          const pathnameAndSearch = `${pathname}${window?.location?.search}`
          return router.push(`/consent-form?destination=${pathnameAndSearch}`)
        }
      } else {
        // if has cookie from login form, use it
        const redirectUrlFromLoginForm = getCookie(
          'bhq_login_form_redirect_url'
        )
        if (redirectUrlFromLoginForm) {
          deleteCookie('bhq_login_form_redirect_url')
          window.location.href = redirectUrlFromLoginForm
          return
        }
      }
      // if role === better_staff, redirect to /my-account/better-staff
      if (
        userMe?.data?.roles?.[0] === 'better_staff' &&
        !pathname.includes('/my-account/')
      ) {
        return router.replace(useAuth.getState().betterStaffMenu[0].path)
      }
    })()
  }, [pathname])

  // Track all clicks
  useEffect(() => {
    const clickHandler = async (event) => {
      const dataAttr = 'data-bhq-cta'
      // Use the utility function to find the parent with `data-bhq-cta`
      const targetElement = findParentWithDataAttribute(event.target, dataAttr)
      if (targetElement) {
        const userMe = useAuth.getState().userMe
        // if user is not logged in, do not track
        if (!userMe) return
        // Process the click event, log, send to analytics, etc.
        console.log(
          `Event triggered for: ${targetElement.getAttribute(dataAttr)}`
        )
        const eventName = targetElement.getAttribute(dataAttr)
        if (!eventName) return
        const token = await executeRecaptcha('fpd_click_cta')
        // Get previous URL
        const res = await useFpd.getState().logEvent({
          event_name: 'CLICK_CTA',
          event_desc_1: eventName,
          token,
        })
        console.log(res)
      }
    }

    // Add event listener on document
    document.addEventListener('click', clickHandler)

    return () => {
      // Clean up the event listener when the component unmounts
      document.removeEventListener('click', clickHandler)
    }
  }, [])

  // GA quick hack When route change,
  useEffect(() => {
    const hasSearchParam = searchParams?.toString()?.length > 0
    const url = `${pathname}${!!hasSearchParam ? `?${searchParams}` : ''}`
    //get current user agent
    const userAgent = navigator.userAgent
    //check if user agent is google bot or contains 'moto g power' or '10_15_7'
    const isMotoGPower = userAgent?.includes('moto g power')
    if (isMotoGPower) {
      return
    }

    console.log('next_router_change:', url)

    // Add a small delay to ensure GTM is fully loaded
    const timeoutId = setTimeout(() => {
      sendGTMEvent({
        pageType: 'PageView',
        event: 'next_router_change',
        pageTitle: (typeof document !== 'undefined' && document.title) || '',
        pageUrl: url,
        page_path: pathname,
        page_location: url,
        page_title: (typeof document !== 'undefined' && document.title) || '',
      })
    }, 100) // 100ms delay

    return () => clearTimeout(timeoutId)
  }, [pathname, searchParams])

  return (
    <SnackbarProvider
      className="tw-my-2 !tw-flex-nowrap"
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      iconVariant={{
        error: (
          <i
            aria-hidden="true"
            className="fas fa-triangle-exclamation tw-mr-2 tw-text-lg"
          />
        ),
      }}
      autoHideDuration={3000}
      maxSnack={3}
    >
      <SWRConfig
        value={{
          refreshInterval: 0,
          fetcher: (resource) => CMS_INSTANCE(resource).then((d) => d.data),
        }}
      >
        <LocalizationProvider
          dateFormats={dateFormats}
          dateAdapter={AdapterDayjs}
          adapterLocale={locale}
          localeText={{
            okButtonLabel: __t('confirm'),
            cancelButtonLabel: __t('cancel'),
          }}
        >
          {children}
        </LocalizationProvider>
      </SWRConfig>
    </SnackbarProvider>
  )
}

export default ClientStoreInitializer
